import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacySelectionListChange as MatSelectionListChange } from '@angular/material/legacy-list';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { debounceTime, startWith, switchMap } from 'rxjs/operators';
import { EmperorUIQuestion, EmperorUISelectFormChoice } from '../../models';

@UntilDestroy()
@Component({
  selector: 'emperor-searchable-list',
  templateUrl: './searchable-list.component.html',
  styleUrls: ['./searchable-list.component.scss'],
})
export class SearchableListComponent implements OnInit {
  @Input() questionControl: FormControl;
  @Input() question: EmperorUIQuestion;

  filteredChoices$: Observable<EmperorUISelectFormChoice[]>;
  searchControl: FormControl;

  selectAllControl = new FormControl();

  ngOnInit(): void {
    this.setupForm();
    this.handleSelectAllClick();
    this.handleTogglingSelectAll();
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  selectionChange(selections: MatSelectionListChange) {
    const currentSelections = this.question.value || [];
    selections.options.forEach(option => {
      if (option.selected) {
        currentSelections.push(option.value);
        this.questionControl.setValue(currentSelections.sort());
      } else {
        this.questionControl.setValue(currentSelections.filter(cs => cs !== option.value).sort());
      }
    });
  }

  isSelected(selection: string): boolean {
    return this.question.value ? this.question.value.includes(selection) : false;
  }

  trackByChoiceId(index, item: EmperorUISelectFormChoice): string {
    return item.id;
  }

  shouldDisplaySelectAll(filteredChoices: EmperorUISelectFormChoice[]): boolean {
    return filteredChoices.length === this.question.choices.length;
  }

  private setupForm() {
    this.searchControl = new FormControl();
    this.filteredChoices$ = this.searchControl.valueChanges.pipe(
      startWith(null),
      debounceTime(250),
      switchMap((search: string) => {
        if (search === null || '') {
          return of(this.question.choices || []);
        } else {
          return of(this.question.choices?.filter(choice => choice.label.toLocaleLowerCase().includes(search.toLocaleLowerCase())) || []);
        }
      }),
    );
  }

  private handleSelectAllClick(): void {
    this.selectAllControl.valueChanges.pipe(untilDestroyed(this)).subscribe(([value]) => {
      if (value) {
        this.questionControl.setValue(this.question.choices.map(item => item.value));
      } else {
        this.questionControl.setValue([]);
      }
    });
  }

  private handleTogglingSelectAll(): void {
    this.questionControl.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      if (value.length === this.question.choices.length) {
        this.selectAllControl.setValue([true], { emitEvent: false });
      } else {
        this.selectAllControl.setValue([false], { emitEvent: false });
      }
    });
  }
}
